/**
 * Utility functions for countdown calculations
 */

/**
 * Normalize a date to midnight for date-only comparison
 * @param {Date} date - The date to normalize
 * @returns {Date} Normalized date at midnight
 */
export const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * Calculate the time difference between target date and now
 * @param {Date} targetDate - The target date to count down to
 * @param {Date} now - The current date (defaults to new Date())
 * @returns {{years: number, months: number, days: number, isPastDeadline: boolean}}
 */
export const calculateTimeDifference = (targetDate, now = new Date()) => {
  const nowNormalized = normalizeDate(now);
  const targetNormalized = normalizeDate(targetDate);

  const diff = targetNormalized - nowNormalized;

  let years = 0;
  let months = 0;
  let days = 0;
  let isPastDeadline = false;

  if (diff < 0) {
    // Deadline has passed - calculate time since
    isPastDeadline = true;
    let current = normalizeDate(targetDate);

    // Calculate years since
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const nextYear = normalizeDate(new Date(current.getFullYear() + 1, current.getMonth(), current.getDate()));
      if (nextYear <= nowNormalized) {
        years++;
        current = nextYear;
        // If we've exactly reached now, stop
        if (nextYear.getTime() === nowNormalized.getTime()) {
          break;
        }
      } else {
        break;
      }
    }

    // Calculate months since
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const nextMonth = normalizeDate(new Date(current.getFullYear(), current.getMonth() + 1, current.getDate()));
      if (nextMonth <= nowNormalized) {
        months++;
        current = nextMonth;
        // If we've exactly reached now, stop
        if (nextMonth.getTime() === nowNormalized.getTime()) {
          break;
        }
      } else {
        break;
      }
    }

    // Calculate remaining days since
    const daysDiff = Math.floor((nowNormalized - current) / (1000 * 60 * 60 * 24));
    days = Math.max(0, daysDiff);
  } else {
    // Deadline hasn't passed - calculate time remaining
    isPastDeadline = false;
    let current = normalizeDate(now);

    // Calculate years
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const nextYear = normalizeDate(new Date(current.getFullYear() + 1, current.getMonth(), current.getDate()));
      if (nextYear <= targetNormalized) {
        years++;
        current = nextYear;
        // If we've exactly reached the target year, stop
        if (nextYear.getTime() === targetNormalized.getTime()) {
          break;
        }
      } else {
        break;
      }
    }

    // Calculate months
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const nextMonth = normalizeDate(new Date(current.getFullYear(), current.getMonth() + 1, current.getDate()));
      // Use <= so we include the case where nextMonth equals targetNormalized
      // (meaning we've reached the target month, no need to add another month)
      if (nextMonth <= targetNormalized) {
        months++;
        current = nextMonth;
        // If we've exactly reached the target month, stop
        if (nextMonth.getTime() === targetNormalized.getTime()) {
          break;
        }
      } else {
        break;
      }
    }

    // Calculate remaining days
    const daysDiff = Math.floor((targetNormalized - current) / (1000 * 60 * 60 * 24));
    days = Math.max(0, daysDiff);
  }

  return { years, months, days, isPastDeadline };
};

/**
 * Format time data into a human-readable string
 * @param {{years: number, months: number, days: number}} time - The time data to format
 * @param {boolean} isPast - Whether the deadline has passed
 * @returns {string} Formatted time string
 */
export const formatTime = (time, isPast = false) => {
  if (!time) return '';

  const { years, months, days } = time;
  const parts = [];

  if (years > 0) {
    parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  }
  if (months > 0) {
    parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
  }
  if (days > 0 || parts.length === 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }

  const timeString = parts.join(' ');
  return isPast ? `${timeString} ago` : timeString;
};
